/*
 * Photo detail's page is broken up into 3 distinct steps.
 * Each step can be navigated to directly from other screens
 * (documented below).
 *
 * - Step 1: "Get This Photo" button (default)
 * - Step 2: License Picker drawer (redirected after login)
 * - Step 3: Download (redirected after checkout)
 */

import React, { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { parse } from "query-string";

// Components:
import {
    EditorialMessage,
    EditorialName,
    EditorialIcon,
    GetPhotoButton
} from "./styles";
import HeroImage from "./HeroImage";
import Tooltip from "core/Tooltip";
import PhotoDetailDownload from "photos/PhotoDetailDownload";
import PhotoDetailDrawer from "photos/PhotoDetailDrawer";
import ImageDetailHero from "core/ImageDetailHero";

// Helpers
import { UploadLicense } from "js/graphql";
import {
    KEY_PUBLIC_RES,
    KEY_BASIC_RES,
    KEY_LARGE_RES,
    QUERY_OPTIONS,
    QUERY_PURCHASED,
    getValidKey,
    handleGaEvents
} from "photos/helpers";
import colors from "core/colors";
import { PhotosContext } from "photos/context";
import { PHOTO_CHECKOUT, authLinks } from "data/links";
import { useAuth } from "frontend/hooks/useAuth";

const STEP_GET_PHOTO = 1;
const STEP_SELECT_LICENSE = 2;
const STEP_PURCHASED = 3;

const PhotoDetailSteps = ({
    photo,
    photoId // `photo` might be `null` while awaiting apollo, but `photoId` will always be set
}) => {
    const { history } = useContext(PhotosContext);
    const { isLoggedIn, confirmAuth } = useAuth();

    // Allow a query parameter to manipulate which step the user is on.
    const queryString = useLocation().search;
    const {
        [QUERY_PURCHASED]: showPurchased,
        [QUERY_OPTIONS]: showOptions
    } = parse(queryString);
    const initialStep = showPurchased
        ? STEP_PURCHASED
        : showOptions
        ? STEP_SELECT_LICENSE
        : STEP_GET_PHOTO;
    const [step, setStep] = useState(initialStep);

    // To mitigate users bookmarking steps 2 & 3, we remove the query from the URL.
    // - useState will help us remember the query details we're removing.
    // - We need to remember whether this is a paid download vs. basic download.
    //   Because the state value is defined on the initial render (when query is present),
    //   we set the download-type on the first render and do not modify the value later.
    // - We need to remember if the options drawer should pre-select a particular license.
    // - We remove the query without reloading the page, which would reset to step 1.
    const [isPurchased] = useState(getValidKey(showPurchased) !== null);
    const [initialSelection] = useState(
        getValidKey(showOptions) || KEY_LARGE_RES
    );
    useEffect(() => {
        if (showOptions || showPurchased) {
            history.replace(location.pathname);
            handleGaEvents({
                photoId,
                licenseName: getValidKey(showPurchased),
                selectLicense: !!showOptions,
                download: !!showPurchased
            });
        }
    }, []); // run only once, on initial load

    // If the photo ends up being public domain, skip to download step
    const isPublicDomain =
        !!photo && photo.license === UploadLicense.Publicdomain;
    useEffect(() => {
        if (isPublicDomain && isLoggedIn) {
            setStep(STEP_PURCHASED);
            handleGaEvents({
                photoId,
                licenseName: KEY_PUBLIC_RES,
                download: true
            });
        }
    }, [isPublicDomain, isLoggedIn]);

    if (!photo) {
        return null;
    }

    let handleGetPhotoClick = () => {
        if (isPublicDomain) {
            setStep(STEP_PURCHASED);
        } else {
            setStep(STEP_SELECT_LICENSE);
            handleGaEvents({
                photoId,
                selectLicense: true
            });
        }
    };

    let panelContent = null;
    // The "Get This Photo" button should always be displayed unless the download
    // content is shown. We disable the button when the user is not on step 1.
    let primaryContent = (
        <>
            {!!photo.editorialUseOnly && (
                <EditorialMessage>
                    This photo is for{" "}
                    <Tooltip
                        clickToOpen
                        message="Can only be used to illustrate matters of public interest. Not model or property released. Can't be used to advertise or promote a product or service."
                    >
                        <EditorialName>Editorial Use Only</EditorialName>
                        <EditorialIcon
                            size="sm"
                            fillColor={colors.accent.darkMode.suenosBuenos}
                        />
                    </Tooltip>
                </EditorialMessage>
            )}
            <GetPhotoButton
                styleType="cta"
                darkMode
                width="100%"
                isDisabled={step === STEP_SELECT_LICENSE && isLoggedIn}
                onClick={() => {
                    confirmAuth({
                        onSuccess: handleGetPhotoClick
                    });
                }}
            >
                Get This Photo
            </GetPhotoButton>
        </>
    );

    if (step === STEP_SELECT_LICENSE && isLoggedIn) {
        panelContent = (
            <PhotoDetailDrawer
                photo={photo}
                initialSelection={initialSelection}
                onClose={() => setStep(STEP_GET_PHOTO)}
                onSubmit={photoType => {
                    if (photoType === KEY_BASIC_RES) {
                        setStep(STEP_PURCHASED);
                        handleGaEvents({
                            photoId,
                            licenseName: KEY_BASIC_RES,
                            download: true
                        });
                        return;
                    }
                    // navigate to checkout page
                    history.push(
                        PHOTO_CHECKOUT.replace(
                            ":purchaseType",
                            photoType
                        ).replace(":photoId", photo.id)
                    );
                }}
            />
        );
    }

    if (step === STEP_PURCHASED && isLoggedIn) {
        primaryContent = (
            <PhotoDetailDownload
                photo={photo}
                isPurchased={isPurchased}
                onBack={() => {
                    setStep(STEP_SELECT_LICENSE);
                    handleGaEvents({
                        photoId,
                        selectLicense: true
                    });
                }}
            />
        );
    }

    const imageContent = (
        <HeroImage
            src={photo.assets.thumbnailLarge}
            alt={photo.title}
            ratio={photo && photo.ratio}
        >
            {/* <PhotoHeroSaveButton saved /> */}
        </HeroImage>
    );

    return (
        <>
            <ImageDetailHero
                image={photo}
                leftColumnContent={imageContent}
                rightColumnContent={primaryContent}
            />
            {!!panelContent && panelContent}
        </>
    );
};

PhotoDetailSteps.propTypes = {
    photoId: PropTypes.string.isRequired,
    photo: PropTypes.object
};

export default PhotoDetailSteps;
